<template>
  <div class="settings-page scroll-wrapper">
    <div class="settings-shell">
      <div class="settings-header">
        <p class="settings-kicker">Системные параметры</p>
        <h3 class="settings-title">Основные настройки</h3>
        <p class="settings-subtitle">Задайте рабочий часовой пояс для календаря и общих расчётов по системе.</p>
      </div>
      <el-card shadow="always" class="settings-card">
        <el-form :model="form" label-width="200px" class="settings-form">
          <el-form-item label="Часовой пояс">
            <el-select v-model="form.timezone" placeholder="Выберите часовой пояс" filterable>
              <el-option
                v-for="tz in timezones"
                :key="tz"
                :label="timezoneLabels[tz] || tz"
                :value="tz"
              />
            </el-select>
          </el-form-item>
          <el-form-item style="display:none;" label="Интервал между записями">
            <el-select v-model="form.cellInterval" placeholder="Например, 15 минут">
              <el-option :label="'5 минут'" :value="5" />
              <el-option :label="'10 минут'" :value="10" />
              <el-option :label="'15 минут'" :value="15" />
              <el-option :label="'20 минут'" :value="20" />
              <el-option :label="'30 минут'" :value="30" />
              <el-option :label="'60 минут'" :value="60" />
            </el-select>
          </el-form-item>
          <el-form-item class="settings-form__submit">
            <el-button type="primary" @click="saveTimezone">Сохранить</el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>
  </div>
</template>

<style scoped>
.settings-page {
  height: 100%;
  overflow-y: auto;
  padding: 24px 18px 28px;
  background:
    radial-gradient(circle at top left, rgba(22, 61, 124, 0.28), transparent 34%),
    linear-gradient(180deg, #091325 0%, #07111f 100%);
}

.settings-shell {
  width: min(920px, 100%);
  margin: 0 auto;
}

.settings-header {
  margin-bottom: 18px;
}

.settings-kicker {
  margin: 0 0 6px;
  color: rgba(174, 194, 230, 0.64);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
}

.settings-title {
  margin: 0 0 6px;
  color: #f5f9ff;
  font-family: var(--ui-font-family-base);
  font-size: 32px;
  font-weight: 500;
}

.settings-subtitle {
  margin: 0;
  color: #9bb4d8;
  font-size: 14px;
  line-height: 1.5;
}

.settings-card {
  border: 1px solid rgba(110, 146, 214, 0.18);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(10, 26, 52, 0.96), rgba(7, 18, 36, 0.94));
  box-shadow: 0 24px 48px rgba(2, 8, 18, 0.34);
}

.settings-card :deep(.el-card__body) {
  padding: 22px;
}

.settings-form :deep(.el-form-item__label) {
  color: #eef5ff;
}

.settings-form :deep(.el-input__wrapper),
.settings-form :deep(.el-select__wrapper) {
  background: rgba(10, 26, 52, 0.96) !important;
  box-shadow: inset 0 0 0 1px rgba(110, 146, 214, 0.16) !important;
}

.settings-form :deep(.el-input__inner),
.settings-form :deep(.el-select__placeholder) {
  color: #eef5ff !important;
}

.settings-form__submit :deep(.el-form-item__content) {
  width: calc(100% - 200px);
  margin-left: 200px !important;
}

.settings-form :deep(.el-button--primary) {
  min-height: 42px;
  width: 100%;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #22c0ff, #2b7cff);
  box-shadow: 0 16px 32px rgba(0, 123, 255, 0.2);
}

@media (max-width: 768px) {
  .settings-page {
    padding: 16px 12px 22px;
  }

  .settings-title {
    font-size: 28px;
  }

  .settings-card :deep(.el-card__body) {
    padding: 16px;
  }

  .settings-form {
    --el-form-label-width: 100% !important;
  }

  .settings-form :deep(.el-form-item) {
    display: block;
  }

  .settings-form :deep(.el-form-item__label) {
    margin-bottom: 8px;
    line-height: 1.4;
    text-align: center;
    justify-content: center;
    width: 100%;
  }

  .settings-form :deep(.el-form-item__content) {
    width: 100%;
    margin-left: 0 !important;
    justify-content: stretch;
  }

  .settings-form :deep(.el-select) {
    width: 100%;
  }

  .settings-form :deep(.el-button--primary) {
    width: 100%;
  }
}
</style>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'SettingsProfile',
  data() {
    const timezoneLabels = {
      'Europe/Kaliningrad': 'Калининград (GMT+2)',
      'Europe/Moscow': 'Москва (GMT+3)',
      'Europe/Samara': 'Самара (GMT+4)',
      'Asia/Yekaterinburg': 'Екатеринбург (GMT+5)',
      'Asia/Omsk': 'Омск (GMT+6)',
      'Asia/Novosibirsk': 'Новосибирск (GMT+7)',
      'Asia/Krasnoyarsk': 'Красноярск (GMT+7)',
      'Asia/Irkutsk': 'Иркутск (GMT+8)',
      'Asia/Yakutsk': 'Якутск (GMT+9)',
      'Asia/Vladivostok': 'Владивосток (GMT+10)',
      'Asia/Sakhalin': 'Сахалин (GMT+11)',
      'Asia/Magadan': 'Магадан (GMT+11)',
      'Asia/Kamchatka': 'Камчатка (GMT+12)',
      'Asia/Almaty': 'Алматы (GMT+6)',
      'Asia/Aqtobe': 'Актобе (GMT+5)',
      'Asia/Baku': 'Баку (GMT+4)',
      'Asia/Tbilisi': 'Тбилиси (GMT+4)',
      'Asia/Yerevan': 'Ереван (GMT+4)',
      'Asia/Bishkek': 'Бишкек (GMT+6)',
      'Asia/Tashkent': 'Ташкент (GMT+5)',
      'Asia/Ashgabat': 'Ашхабад (GMT+5)',
      'Europe/Minsk': 'Минск (GMT+3)',
      'Asia/Dushanbe': 'Душанбе (GMT+5)',
      'Europe/Kiev': 'Киев (GMT+3)',
      'Europe/Chisinau': 'Кишинёв (GMT+3)',
    };
    return {
      form: {
        timezone: '',
        cellInterval: 15
      },
      timezoneLabels,
      timezones: Object.keys(timezoneLabels)
    }
  },
  computed:{
    ...mapState('settings', ['setting'])
  },
  methods:{
    ...mapActions('settings', ['getSettings', 'updateSettings']),
    async saveTimezone() {
      await this.updateSettings({
        timezone: this.form.timezone,
        cellInterval: this.form.cellInterval
      })
      this.$message.success('Настройки сохранены')
    }
  },
  mounted() {
    this.getSettings().then(() => {
      const tz = this.setting.timezone
      this.form.timezone = this.timezones.includes(tz) ? tz : 'Europe/Moscow'
      this.form.cellInterval = this.setting.cellInterval || 15
    })
  }
}
</script>

